import { dismissToast, useToasts, type ToastTone } from '@web/hooks/useToast';
import { cn } from '@web/lib/cn';

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-rule-strong bg-raised text-ink',
  warn: 'border-warm bg-raised text-ink',
  error: 'border-signal bg-raised text-ink',
};

const TONE_MARK: Record<ToastTone, string> = {
  info: 'bg-ink-faint',
  warn: 'bg-warm',
  error: 'bg-signal',
};

/**
 * Top-centre, because on a phone the bottom of the screen is the composer and
 * the thumb. Assertive: the two things that reach this surface are "you lost a
 * claim" and "that send failed", and both are answers to something the operator
 * just did — waiting politely for a pause would tell them too late.
 */
export function Toaster() {
  const toasts = useToasts();
  if (toasts.length === 0) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-px pt-2"
    >
      {toasts.map((item) => (
        <div
          key={item.id}
          className={cn(
            'pointer-events-auto flex w-[min(92vw,26rem)] items-start gap-3 border px-3 py-2 text-xs',
            TONE_CLASS[item.tone],
          )}
        >
          <span aria-hidden className={cn('mt-[5px] h-2 w-[3px] shrink-0', TONE_MARK[item.tone])} />
          <div className="min-w-0 flex-1">
            <div className="font-medium">{item.text}</div>
            {item.detail !== undefined && <div className="mt-0.5 text-ink-dim">{item.detail}</div>}
          </div>
          <button
            type="button"
            className="-my-1 -mr-1 flex size-6 items-center justify-center font-mono text-ink-faint hover:text-ink"
            aria-label="Dismiss"
            onClick={() => {
              dismissToast(item.id);
            }}
          >
            <span aria-hidden>×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
